import React, { useState } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
  Badge,
  Avatar,
  Tooltip,
  Paper,
  Stack,
  Chip,
  alpha,
  ListSubheader,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Grid,
  FormControlLabel,
  Switch,
  CircularProgress,
  Snackbar,
  Alert,
  Checkbox,
  DialogContentText
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import {
  Menu as MenuIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Notifications as NotificationsIcon,
  CardGiftcard as CardGiftcardIcon,
  Person as PersonIcon,
  Inventory as InventoryIcon,
  People as PeopleIcon,
  Share as ShareIcon,
  Campaign as CampaignIcon,
  SmartToy as SmartToyIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  Message as MessageIcon,
  Dashboard as DashboardIcon,
  Psychology as PsychologyIcon,
  Timeline as TimelineIcon,
  Home as HomeIcon,
  Event as EventIcon,
  AutoFixHigh as AutoFixHighIcon,
  AddPhotoAlternate as AddPhotoIcon,
  MusicNote as TikTokIcon,
  Info as InfoIcon,
  TrendingUp as TrendingUpIcon,
  Link as LinkIcon,
  ShoppingCart as ShoppingCartIcon,
  Help as HelpIcon,
  Chat as ChatIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Workspaces as WorkspacesIcon,
  ChevronLeft as ChevronLeftIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../redux/store';
import * as api from '../services/api';
import Logo from './Logo';
import { updateUserData, PlanType } from '../redux/slices/userSlice';
import { useNotifications } from '../contexts/NotificationContext';
import { useEvents } from '../contexts/EventContext';
import { MainContentBox, ContentContainer } from '../styles/LayoutStyles';

const drawerWidth = 240;

interface LayoutProps {
  children: React.ReactNode;
}

const blinkAnimation = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
`;

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_FILES = 5;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif'];

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const leads = useSelector((state: RootState) => state.leads.items);
  const products = useSelector((state: RootState) => state.products.items);
  const userData = useSelector((state: RootState) => state.user?.data || {
    name: 'João Silva',
    avatar: null
  });
  
  const [openWhatsAppDialog, setOpenWhatsAppDialog] = useState(false);
  const [openEmailDialog, setOpenEmailDialog] = useState(false);
  const [selectedLead, setSelectedLead] = useState('');
  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [messageTemplate, setMessageTemplate] = useState('custom');
  const [manualPhone, setManualPhone] = useState('');
  const [manualEmail, setManualEmail] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [includeProductLink, setIncludeProductLink] = useState(false);
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [isImprovingText, setIsImprovingText] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('info');
  const [autoImproveWithAI, setAutoImproveWithAI] = useState(false);
  const dispatch = useDispatch();
  const [open, setOpen] = useState(!isMobile);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationsAnchor, setNotificationsAnchor] = useState<null | HTMLElement>(null);
  const [planMenuAnchor, setPlanMenuAnchor] = useState<null | HTMLElement>(null);
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [selectedNewPlan, setSelectedNewPlan] = useState<PlanType | null>(null);
  const [planStatusAnchor, setPlanStatusAnchor] = useState<null | HTMLElement>(null);
  const { unreadCount } = useNotifications();
  const { newEventsCount, markAllEventsAsRead } = useEvents();

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationsClick = () => {
    navigate('/notificacoes');
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchor(null);
  };

  const handlePlanClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setPlanMenuAnchor(event.currentTarget);
  };

  const handlePlanClose = () => {
    setPlanMenuAnchor(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    // Implementar lógica de logout
  };

  const handlePlanStatusClick = (event: React.MouseEvent<HTMLElement>) => {
    setPlanStatusAnchor(event.currentTarget);
  };

  const handlePlanStatusClose = () => {
    setPlanStatusAnchor(null);
  };

  const handleUpgradePlan = () => {
    handlePlanStatusClose();
    navigate('/planos');
  };

  const handleProfileClick = () => {
    handleMenuClose();
    navigate('/perfil');
  };

  const handleSettingsClick = () => {
    handleMenuClose();
    navigate('/configuracoes');
  };

  const handleEventsClick = () => {
    navigate('/eventos');
    markAllEventsAsRead();
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/', color: '#1565c0' },
    { text: 'Produtos', icon: <InventoryIcon />, path: '/produtos', color: '#f44336' },
    { text: 'Leads', icon: <PeopleIcon />, path: '/leads', color: '#2196f3' },
    { text: 'Eventos', icon: <EventIcon />, path: '/eventos', color: '#4caf50' },
    { text: 'Redes Sociais', icon: <ShareIcon />, path: '/redes-sociais', color: '#9c27b0' },
    { text: 'Marketing', icon: <CampaignIcon />, path: '/marketing', color: '#ff9800' },
    { text: 'Chat IA', icon: <SmartToyIcon />, path: '/chat-ia', color: '#9c27b0' },
    { text: 'WhatsApp IA Bot', icon: <WhatsAppIcon />, path: '/whatsapp-ai', color: '#00796b' },
    { text: 'E-mail Response', icon: <EmailIcon />, path: '/email-response', color: '#0288d1' },
    { text: 'Status', icon: <AssessmentIcon />, path: '/status', color: '#1976d2' },
    { text: 'Notificações', icon: <NotificationsIcon />, path: '/notificacoes', color: '#d32f2f' },
    { text: 'Indicar Amigo', icon: <CardGiftcardIcon />, path: '/indicar-amigo', color: '#9c27b0' },
    { text: 'Suporte', icon: <HelpIcon />, path: '/suporte', color: '#455a64' },
    { text: 'Planos', icon: <StarIcon />, path: '/planos', color: '#f57c00' },
    { text: 'Configurações', icon: <SettingsIcon />, path: '/configuracoes', color: '#757575' },
  ];

  const handleOpenWhatsAppDialog = () => {
    setOpenWhatsAppDialog(true);
    setMessage('Olá! Gostaria de saber mais sobre nossos produtos e serviços.');
    setMessageTemplate('custom');
    setSelectedProduct('');
    setIncludeProductLink(false);
    setSelectedImages([]);
    setPreviewUrls([]);
  };

  const handleOpenEmailDialog = () => {
    setOpenEmailDialog(true);
    setSubject('Informações sobre produtos e serviços');
    setMessage('Olá!\n\nGostaria de receber mais informações sobre seus produtos e serviços.\n\nAtenciosamente,');
    setMessageTemplate('custom');
    setSelectedProduct('');
    setIncludeProductLink(false);
    setSelectedImages([]);
    setPreviewUrls([]);
  };

  const handleCloseWhatsAppDialog = () => {
    setOpenWhatsAppDialog(false);
    setSelectedLead('');
    setMessage('');
    setManualPhone('');
    setSelectedProduct('');
    setIncludeProductLink(false);
    setSelectedImages([]);
    setPreviewUrls([]);
  };

  const handleCloseEmailDialog = () => {
    setOpenEmailDialog(false);
    setSelectedLead('');
    setSubject('');
    setMessage('');
    setManualEmail('');
    setSelectedProduct('');
    setIncludeProductLink(false);
    setSelectedImages([]);
    setPreviewUrls([]);
  };

  const handleLeadChange = (event: SelectChangeEvent) => {
    setSelectedLead(event.target.value);
  };

  const handleMessageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(event.target.value);
  };

  const handleSubjectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSubject(event.target.value);
  };

  const handleTemplateChange = async (event: SelectChangeEvent) => {
    const template = event.target.value;
    setMessageTemplate(template);
    
    let newMessage = '';
    let newSubject = '';
    
    switch (template) {
      case 'followup':
        newMessage = 'Olá! Estou entrando em contato para saber se você tem alguma dúvida sobre nossos produtos ou serviços. Estou à disposição para ajudar.';
        newSubject = 'Acompanhamento - Produtos e Serviços';
        break;
      case 'promo':
        newMessage = 'Olá! Temos uma promoção especial para você! Nossos produtos estão com 20% de desconto até o final da semana. Aproveite esta oportunidade!';
        newSubject = 'Promoção Especial - 20% de Desconto';
        break;
      case 'thanks':
        newMessage = 'Olá! Gostaria de agradecer pelo seu interesse em nossos produtos e serviços. Estamos à disposição para qualquer dúvida ou suporte que precisar.';
        newSubject = 'Agradecimento - Vendedor Virtual';
        break;
      default:
        newMessage = 'Olá! Gostaria de saber mais sobre nossos produtos e serviços.';
        newSubject = 'Informações sobre produtos e serviços';
        break;
    }
    
    setMessage(newMessage);
    
    if (openEmailDialog) {
      setSubject(newSubject);
    }
    
    // Se a opção de melhoria automática estiver ativada, melhora o texto com IA
    if (autoImproveWithAI && template !== 'custom') {
      try {
        setIsImprovingText(true);
        const type = openWhatsAppDialog ? 'whatsapp' : 'email';
        const response = await api.improveTextWithAI(newMessage, type);
        
        if (response.data && response.data.improvedText) {
          setMessage(response.data.improvedText);
          setSnackbarMessage('Texto melhorado automaticamente com IA!');
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
        }
      } catch (error) {
        console.error('Erro ao melhorar texto automaticamente:', error);
      } finally {
        setIsImprovingText(false);
      }
    }
  };

  // Adicionar função de validação de telefone
  const validatePhone = (phone: string) => {
    // Remove todos os caracteres não numéricos
    const numbers = phone.replace(/\D/g, '');
    // Verifica se tem entre 10 e 11 dígitos (com DDD)
    return numbers.length >= 10 && numbers.length <= 11;
  };

  // Função para formatar o número de telefone
  const formatPhone = (phone: string) => {
    let numbers = phone.replace(/\D/g, '');
    if (numbers.length === 11) {
      return `(${numbers.slice(0,2)}) ${numbers.slice(2,7)}-${numbers.slice(7)}`;
    } else if (numbers.length === 10) {
      return `(${numbers.slice(0,2)}) ${numbers.slice(2,6)}-${numbers.slice(6)}`;
    }
    return phone;
  };

  const handleManualPhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const formattedPhone = formatPhone(event.target.value);
    setManualPhone(formattedPhone);
  };

  const handleManualEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setManualEmail(event.target.value);
  };

  const handleProductChange = (event: SelectChangeEvent) => {
    setSelectedProduct(event.target.value);
  };

  const handleProductLinkChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIncludeProductLink(event.target.checked);
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const files = Array.from(event.target.files);

      // Validar número máximo de arquivos
      if (selectedImages.length + files.length > MAX_FILES) {
        setSnackbarMessage(`Você pode enviar no máximo ${MAX_FILES} imagens.`);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }

      // Validar cada arquivo
      const validFiles = files.filter(file => {
        // Verificar tipo de arquivo
        if (!ALLOWED_TYPES.includes(file.type)) {
          setSnackbarMessage(`Arquivo "${file.name}" não é uma imagem válida.`);
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
          return false;
        }

        // Verificar tamanho do arquivo
        if (file.size > MAX_FILE_SIZE) {
          setSnackbarMessage(`Arquivo "${file.name}" excede o tamanho máximo de 5MB.`);
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
          return false;
        }

        return true;
      });

      if (validFiles.length > 0) {
        setSelectedImages(prevImages => [...prevImages, ...validFiles]);
      
      // Criar URLs para preview
        const newPreviewUrls = validFiles.map(file => URL.createObjectURL(file));
      setPreviewUrls(prevUrls => [...prevUrls, ...newPreviewUrls]);

        setSnackbarMessage('Imagens carregadas com sucesso!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }
    }
  };

  const handleRemoveImage = (index: number) => {
    try {
    // Liberar URL do objeto para evitar vazamento de memória
    URL.revokeObjectURL(previewUrls[index]);
    
    setSelectedImages(prevImages => prevImages.filter((_, i) => i !== index));
    setPreviewUrls(prevUrls => prevUrls.filter((_, i) => i !== index));

      setSnackbarMessage('Imagem removida com sucesso.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Erro ao remover imagem:', error);
      setSnackbarMessage('Erro ao remover imagem. Tente novamente.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const getProductLink = (productId: string) => {
    // URL base do seu aplicativo + rota do produto
    const baseUrl = window.location.origin;
    return `${baseUrl}/produtos/${productId}`;
  };

  const handleAddProductLink = () => {
    if (selectedProduct) {
      const product = products.find(p => p.id === selectedProduct);
      if (product) {
        const productLink = getProductLink(product.id);
        const productInfo = `\n\nProduto: ${product.name}\nPreço: R$ ${product.price.toFixed(2)}\nLink: ${productLink}`;
        setMessage(prevMessage => prevMessage + productInfo);
      }
    }
  };

  const handleSendWhatsApp = () => {
    let phoneNumber = '';
    let text = encodeURIComponent(message);
    
    if (selectedLead) {
      const lead = leads.find(l => l.id === selectedLead);
      if (lead && lead.phone) {
        phoneNumber = lead.phone.replace(/\D/g, '');
      }
    } else if (manualPhone) {
      phoneNumber = manualPhone.replace(/\D/g, '');
    }

    // Validar número de telefone
    if (!validatePhone(phoneNumber)) {
      setSnackbarMessage('Número de telefone inválido. Digite um número com DDD.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
      
      // Garantir que o número comece com o código do país (Brasil: 55)
      if (!phoneNumber.startsWith('55')) {
        phoneNumber = '55' + phoneNumber;
    }
    
    // Adicionar link do produto se solicitado
    if (includeProductLink && selectedProduct) {
      const product = products.find(p => p.id === selectedProduct);
      if (product) {
        const productLink = getProductLink(product.id);
        text = encodeURIComponent(message + `\n\nLink do produto: ${productLink}`);
      }
    }
    
    // Abrir WhatsApp Web ou App
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${text}`;
    window.open(whatsappUrl, '_blank');
    
    handleCloseWhatsAppDialog();
  };

  // Adicionar função de validação de email
  const validateEmail = (email: string) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(email);
  };

  const handleSendEmail = () => {
    let email = '';
    let body = encodeURIComponent(message);
    let subjectEncoded = encodeURIComponent(subject);
    
    if (selectedLead) {
      const lead = leads.find(l => l.id === selectedLead);
      if (lead && lead.email) {
        email = lead.email;
      }
    } else if (manualEmail) {
      email = manualEmail;
    }

    // Validar email
    if (!validateEmail(email)) {
      setSnackbarMessage('Endereço de e-mail inválido.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    // Validar assunto
    if (!subject.trim()) {
      setSnackbarMessage('Por favor, preencha o assunto do e-mail.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    // Validar mensagem
    if (!message.trim()) {
      setSnackbarMessage('Por favor, escreva uma mensagem.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    
    // Adicionar link do produto se solicitado
    if (includeProductLink && selectedProduct) {
      const product = products.find(p => p.id === selectedProduct);
      if (product) {
        const productLink = getProductLink(product.id);
        body = encodeURIComponent(message + `\n\nLink do produto: ${productLink}`);
      }
    }
    
    try {
    // Abrir cliente de email padrão
    const mailtoUrl = `mailto:${email}?subject=${subjectEncoded}&body=${body}`;
    window.location.href = mailtoUrl;
    
    handleCloseEmailDialog();
      
      setSnackbarMessage('E-mail aberto no seu cliente de e-mail padrão.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Erro ao abrir cliente de e-mail:', error);
      setSnackbarMessage('Erro ao abrir seu cliente de e-mail. Tente novamente.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleImproveTextWithAI = async (type: 'email' | 'whatsapp') => {
    if (!message) {
      setSnackbarMessage('Por favor, escreva uma mensagem antes de melhorá-la com IA.');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    setIsImprovingText(true);
    
    try {
      const response = await api.improveTextWithAI(message, type);
      
      if (response.data && response.data.improvedText) {
        setMessage(response.data.improvedText);
        setSnackbarMessage('Texto melhorado com sucesso!');
        setSnackbarSeverity('success');
      } else {
        throw new Error('Resposta inválida da API');
      }
    } catch (error) {
      console.error('Erro ao melhorar texto com IA:', error);
      setSnackbarMessage('Não foi possível melhorar o texto. Tente novamente mais tarde.');
      setSnackbarSeverity('error');
    } finally {
      setIsImprovingText(false);
      setSnackbarOpen(true);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleAutoImproveChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAutoImproveWithAI(event.target.checked);
  };

  const handlePlanSelect = (plan: PlanType) => {
    setSelectedNewPlan(plan);
    setShowPlanDialog(true);
    handlePlanClose();
  };

  const handleConfirmPlanChange = () => {
    if (selectedNewPlan) {
      dispatch(updateUserData({ plan: selectedNewPlan }));
      setShowPlanDialog(false);
      setSelectedNewPlan(null);
      
      // Mostrar mensagem de sucesso
      setSnackbarMessage(`Plano alterado com sucesso para ${selectedNewPlan}!`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    }
  };

  const handleCancelPlanChange = () => {
    setShowPlanDialog(false);
    setSelectedNewPlan(null);
  };

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          Vendedor Virtual
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem
            button
            key={item.text}
            onClick={() => navigate(item.path)}
            selected={location.pathname === item.path}
            sx={{
              '&.Mui-selected': {
                backgroundColor: `${item.color}15`,
                '&:hover': {
                  backgroundColor: `${item.color}25`,
                },
                '& .MuiListItemIcon-root': {
                  color: item.color,
                },
                '& .MuiListItemText-primary': {
                  color: item.color,
                  fontWeight: 'bold',
                },
              },
              '&:hover': {
                backgroundColor: `${item.color}10`,
              },
              '& .MuiListItemIcon-root': {
                color: `${item.color}80`,
              },
            }}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${open ? drawerWidth : 0}px)` },
          ml: { sm: `${open ? drawerWidth : 0}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {menuItems.find(item => item.path === location.pathname)?.text || 'Vendedor Virtual'}
          </Typography>
          <Tooltip title="Dashboard">
            <IconButton color="inherit" onClick={() => navigate('/')}>
              <DashboardIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eventos">
            <IconButton color="inherit" onClick={handleEventsClick}>
              <Badge badgeContent={newEventsCount} color="error">
                <EventIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          <Tooltip title={`Plano Atual: ${userData.plan}`}>
            <IconButton 
              color="inherit" 
              onClick={handlePlanStatusClick}
              sx={{ 
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: userData.plan === 'Pro' 
                    ? '#4caf50' 
                    : userData.plan === 'Basic' 
                      ? '#2196f3' 
                      : '#ff9800',
                  top: 8,
                  right: 8,
                  border: '2px solid white'
                }
              }}
            >
              <CardGiftcardIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Notificações">
            <IconButton color="inherit" onClick={handleNotificationsClick}>
              <Badge badgeContent={unreadCount} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          <Tooltip title="Perfil">
            <IconButton color="inherit" onClick={handleMenuClick}>
              <Avatar sx={{ width: 32, height: 32 }} src={userData.avatar || undefined}>
                {userData.name.charAt(0)}
              </Avatar>
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        {isMobile ? (
          <Drawer
            variant="temporary"
            open={open}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: 'block', sm: 'none' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
            }}
          >
            {drawer}
          </Drawer>
        ) : (
          <Drawer
            variant="persistent"
            open={open}
            sx={{
              display: { xs: 'none', sm: 'block' },
              '& .MuiDrawer-paper': { 
                boxSizing: 'border-box', 
                width: drawerWidth,
                borderRight: 'none'
              },
            }}
          >
            {drawer}
          </Drawer>
        )}
      </Box>
      <div 
        style={{
          flexGrow: 1,
          padding: '24px 24px 24px 0',
          width: `calc(100% - ${open ? drawerWidth : 0}px)`,
          margin: 'auto',
          boxSizing: 'border-box',
          borderLeft: '1px solid rgba(0, 0, 0, 0.12)',
        }}
      >
        <Toolbar />
        <div style={{ 
          width: '100%', 
          maxWidth: '1400px',
          display: 'flex',
          flexDirection: 'column',
          paddingLeft: 0
        }}>
          {children}
        </div>
      </div>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            width: 300,
            mt: 1.5,
            '& .MuiMenuItem-root': {
              px: 2,
              py: 1,
            },
          },
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Avatar
              src={userData.avatar || undefined}
              sx={{ width: 40, height: 40, mr: 2 }}
            >
              {userData.name.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {userData.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {userData.role}
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {userData.email}
          </Typography>
        </Box>
        <MenuItem onClick={handleProfileClick}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Meu Perfil" />
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Sair" />
        </MenuItem>
      </Menu>
      <Menu
        anchorEl={notificationsAnchor}
        open={Boolean(notificationsAnchor)}
        onClose={handleNotificationsClose}
      >
        {/* Removendo o conteúdo do menu de notificações */}
      </Menu>
      <Menu
        anchorEl={planMenuAnchor}
        open={Boolean(planMenuAnchor)}
        onClose={handlePlanClose}
      >
        <MenuItem onClick={handlePlanClose}>Plano Básico</MenuItem>
        <MenuItem onClick={handlePlanClose}>Plano Pro</MenuItem>
        <MenuItem onClick={handlePlanClose}>Plano Enterprise</MenuItem>
      </Menu>
      <Menu
        anchorEl={planStatusAnchor}
        open={Boolean(planStatusAnchor)}
        onClose={handlePlanStatusClose}
      >
        <MenuItem>
          <ListItemIcon>
            <CardGiftcardIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText 
            primary={`Plano Atual: ${userData.plan}`} 
            secondary={`${userData.planFeatures.maxLeads === -1 ? 'Leads Ilimitados' : `${userData.planFeatures.maxLeads} Leads`}`}
          />
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleUpgradePlan}>
          <ListItemIcon>
            <TrendingUpIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Fazer Upgrade" />
        </MenuItem>
        <MenuItem onClick={handlePlanStatusClose}>
          <ListItemIcon>
            <InfoIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Ver Detalhes" />
        </MenuItem>
      </Menu>
      
      {/* Dialog para WhatsApp */}
      <Dialog open={openWhatsAppDialog} onClose={handleCloseWhatsAppDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Enviar Mensagem via WhatsApp</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel id="lead-select-label">Selecionar Lead</InputLabel>
            <Select
              labelId="lead-select-label"
              value={selectedLead}
              onChange={handleLeadChange}
              label="Selecionar Lead"
            >
              <MenuItem value="">
                <em>Nenhum (inserir manualmente)</em>
              </MenuItem>
              {leads.map((lead) => (
                <MenuItem key={lead.id} value={lead.id} disabled={!lead.phone}>
                  {lead.name} {lead.phone ? `- ${lead.phone}` : '(sem telefone)'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {!selectedLead && (
            <TextField
              margin="normal"
              label="Número de Telefone"
              fullWidth
              value={manualPhone}
              onChange={handleManualPhoneChange}
              placeholder="Ex: (11) 98765-4321"
              helperText="Inclua o código de área. O código do país (55) será adicionado automaticamente."
            />
          )}
          
          <FormControl fullWidth margin="normal">
            <InputLabel id="template-select-label">Modelo de Mensagem</InputLabel>
            <Select
              labelId="template-select-label"
              value={messageTemplate}
              onChange={handleTemplateChange}
              label="Modelo de Mensagem"
            >
              <MenuItem value="custom">Personalizada</MenuItem>
              <MenuItem value="followup">Acompanhamento</MenuItem>
              <MenuItem value="promo">Promoção</MenuItem>
              <MenuItem value="thanks">Agradecimento</MenuItem>
            </Select>
          </FormControl>
          
          <FormControlLabel
            control={
              <Checkbox
                checked={autoImproveWithAI}
                onChange={handleAutoImproveChange}
                color="secondary"
              />
            }
            label="Melhorar automaticamente com IA ao selecionar modelo"
            sx={{ mt: 1, mb: 1 }}
          />
          
          <TextField
            margin="normal"
            label="Mensagem"
            multiline
            rows={4}
            fullWidth
            value={message}
            onChange={handleMessageChange}
          />
          
          <Button
            variant="outlined"
            color="secondary"
            startIcon={isImprovingText ? <CircularProgress size={20} color="secondary" /> : <AutoFixHighIcon />}
            fullWidth
            sx={{ mt: 1, mb: 2 }}
            onClick={() => handleImproveTextWithAI('whatsapp')}
            disabled={isImprovingText || !message}
          >
            {isImprovingText ? 'Melhorando...' : 'Melhorar com IA'}
          </Button>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Informações do Produto
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="product-select-label">Selecionar Produto</InputLabel>
                <Select
                  labelId="product-select-label"
                  value={selectedProduct}
                  onChange={handleProductChange}
                  label="Selecionar Produto"
                >
                  <MenuItem value="">
                    <em>Nenhum</em>
                  </MenuItem>
                  {products.map((product) => (
                    <MenuItem key={product.id} value={product.id}>
                      {product.name} - R$ {product.price.toFixed(2)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={includeProductLink}
                    onChange={handleProductLinkChange}
                    disabled={!selectedProduct}
                  />
                }
                label="Incluir link do produto na mensagem"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Button
                variant="outlined"
                startIcon={<LinkIcon />}
                onClick={handleAddProductLink}
                disabled={!selectedProduct}
                sx={{ mr: 1 }}
              >
                Adicionar Detalhes do Produto
              </Button>
              
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="upload-whatsapp-image"
                type="file"
                multiple
                onChange={handleImageUpload}
              />
              <label htmlFor="upload-whatsapp-image">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<AddPhotoIcon />}
                >
                  Carregar Fotos
                </Button>
              </label>
            </Grid>
            
            {previewUrls.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Fotos Selecionadas:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {previewUrls.map((url, index) => (
                    <Box key={index} sx={{ position: 'relative' }}>
                      <img 
                        src={url} 
                        alt={`Preview ${index}`} 
                        style={{ 
                          width: 80, 
                          height: 80, 
                          objectFit: 'cover', 
                          borderRadius: 4 
                        }} 
                      />
                      <Chip
                        label="Remover"
                        size="small"
                        onDelete={() => handleRemoveImage(index)}
                        sx={{ 
                          position: 'absolute', 
                          bottom: 5, 
                          right: 5,
                          backgroundColor: 'rgba(0,0,0,0.7)',
                          color: 'white',
                          '& .MuiChip-deleteIcon': {
                            color: 'white'
                          }
                        }}
                      />
                    </Box>
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Nota: As imagens serão enviadas como anexos quando disponível.
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseWhatsAppDialog}>Cancelar</Button>
          <Button 
            onClick={handleSendWhatsApp} 
            variant="contained" 
            color="success"
            startIcon={<WhatsAppIcon />}
            disabled={(selectedLead !== '' && !leads.find(l => l.id === selectedLead)?.phone) || 
                     (selectedLead === '' && !manualPhone)}
          >
            Enviar WhatsApp
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialog para E-mail */}
      <Dialog open={openEmailDialog} onClose={handleCloseEmailDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Enviar E-mail</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel id="lead-email-select-label">Selecionar Lead</InputLabel>
            <Select
              labelId="lead-email-select-label"
              value={selectedLead}
              onChange={handleLeadChange}
              label="Selecionar Lead"
            >
              <MenuItem value="">
                <em>Nenhum (inserir manualmente)</em>
              </MenuItem>
              {leads.map((lead) => (
                <MenuItem key={lead.id} value={lead.id}>
                  {lead.name} - {lead.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {!selectedLead && (
            <TextField
              margin="normal"
              label="E-mail"
              type="email"
              fullWidth
              value={manualEmail}
              onChange={handleManualEmailChange}
              placeholder="Ex: contato@exemplo.com"
            />
          )}
          
          <FormControl fullWidth margin="normal">
            <InputLabel id="template-email-select-label">Modelo de E-mail</InputLabel>
            <Select
              labelId="template-email-select-label"
              value={messageTemplate}
              onChange={handleTemplateChange}
              label="Modelo de E-mail"
            >
              <MenuItem value="custom">Personalizado</MenuItem>
              <MenuItem value="followup">Acompanhamento</MenuItem>
              <MenuItem value="promo">Promoção</MenuItem>
              <MenuItem value="thanks">Agradecimento</MenuItem>
            </Select>
          </FormControl>
          
          <FormControlLabel
            control={
              <Checkbox
                checked={autoImproveWithAI}
                onChange={handleAutoImproveChange}
                color="secondary"
              />
            }
            label="Melhorar automaticamente com IA ao selecionar modelo"
            sx={{ mt: 1, mb: 1 }}
          />
          
          <TextField
            margin="normal"
            label="Assunto"
            fullWidth
            value={subject}
            onChange={handleSubjectChange}
          />
          
          <TextField
            margin="normal"
            label="Mensagem"
            multiline
            rows={6}
            fullWidth
            value={message}
            onChange={handleMessageChange}
          />
          
          <Button
            variant="outlined"
            color="secondary"
            startIcon={isImprovingText ? <CircularProgress size={20} color="secondary" /> : <AutoFixHighIcon />}
            fullWidth
            sx={{ mt: 1, mb: 2 }}
            onClick={() => handleImproveTextWithAI('email')}
            disabled={isImprovingText || !message}
          >
            {isImprovingText ? 'Melhorando...' : 'Melhorar com IA'}
          </Button>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Informações do Produto
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="product-email-select-label">Selecionar Produto</InputLabel>
                <Select
                  labelId="product-email-select-label"
                  value={selectedProduct}
                  onChange={handleProductChange}
                  label="Selecionar Produto"
                >
                  <MenuItem value="">
                    <em>Nenhum</em>
                  </MenuItem>
                  {products.map((product) => (
                    <MenuItem key={product.id} value={product.id}>
                      {product.name} - R$ {product.price.toFixed(2)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={includeProductLink}
                    onChange={handleProductLinkChange}
                    disabled={!selectedProduct}
                  />
                }
                label="Incluir link do produto no e-mail"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Button
                variant="outlined"
                startIcon={<LinkIcon />}
                onClick={handleAddProductLink}
                disabled={!selectedProduct}
                sx={{ mr: 1 }}
              >
                Adicionar Detalhes do Produto
              </Button>
              
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="upload-email-image"
                type="file"
                multiple
                onChange={handleImageUpload}
              />
              <label htmlFor="upload-email-image">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<AddPhotoIcon />}
                >
                  Carregar Fotos
                </Button>
              </label>
            </Grid>
            
            {previewUrls.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Fotos Selecionadas:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {previewUrls.map((url, index) => (
                    <Box key={index} sx={{ position: 'relative' }}>
                      <img 
                        src={url} 
                        alt={`Preview ${index}`} 
                        style={{ 
                          width: 80, 
                          height: 80, 
                          objectFit: 'cover', 
                          borderRadius: 4 
                        }} 
                      />
                      <Chip
                        label="Remover"
                        size="small"
                        onDelete={() => handleRemoveImage(index)}
                        sx={{ 
                          position: 'absolute', 
                          bottom: 5, 
                          right: 5,
                          backgroundColor: 'rgba(0,0,0,0.7)',
                          color: 'white',
                          '& .MuiChip-deleteIcon': {
                            color: 'white'
                          }
                        }}
                      />
                    </Box>
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Nota: As imagens serão enviadas como anexos quando disponível.
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEmailDialog}>Cancelar</Button>
          <Button 
            onClick={handleSendEmail} 
            variant="contained" 
            color="primary"
            startIcon={<EmailIcon />}
            disabled={(selectedLead !== '' && !leads.find(l => l.id === selectedLead)?.email) || 
                     (selectedLead === '' && !manualEmail)}
          >
            Enviar E-mail
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Layout; 